import React from 'react';
import { AlertTriangle, RotateCw } from 'lucide-react';

/**
 * Wraps routed page content so a single component throw (e.g. a missing import
 * or an unexpected API shape) degrades into a friendly, recoverable panel
 * instead of blanking the whole app. Resets itself when `resetKey` changes so
 * navigating to another route clears a previous error.
 */
class RouteErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, message: '' };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, message: error?.message || 'Something went wrong.' };
  }

  componentDidCatch(error, info) {
    // Surface for debugging; production logging can hook in here later.
    console.error('RouteErrorBoundary caught:', error, info?.componentStack);
  }

  componentDidUpdate(prevProps) {
    if (prevProps.resetKey !== this.props.resetKey && this.state.hasError) {
      this.setState({ hasError: false, message: '' });
    }
  }

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <div
        className="flex flex-col items-center justify-center text-center py-24 px-6"
        data-testid="route-error-boundary"
      >
        <div className="h-14 w-14 rounded-2xl bg-rose-50 border border-rose-100 flex items-center justify-center mb-5">
          <AlertTriangle className="h-7 w-7 text-rose-500" />
        </div>
        <h2 className="text-xl font-bold text-slate-900 mb-2">This section hit a snag</h2>
        <p className="text-sm text-slate-500 max-w-md mb-6">
          The page couldn&apos;t finish loading. You can reload it, or head back to the dashboard.
          Your data is safe.
        </p>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => window.location.reload()}
            data-testid="error-reload-btn"
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-teal-700 text-white text-sm font-semibold hover:bg-teal-800 transition-colors"
          >
            <RotateCw className="h-4 w-4" /> Reload page
          </button>
          <button
            type="button"
            onClick={() => { window.location.href = '/dashboard'; }}
            data-testid="error-dashboard-btn"
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border border-slate-200 text-slate-700 text-sm font-semibold hover:bg-slate-50 transition-colors"
          >
            Go to Dashboard
          </button>
        </div>
        {this.state.message && (
          <p className="text-[11px] text-slate-400 mt-6 font-mono max-w-md truncate" data-testid="error-detail">
            {this.state.message}
          </p>
        )}
      </div>
    );
  }
}

export default RouteErrorBoundary;
